import React, { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import { decodeToken } from "../utils/authUtils";

const SessionContext = createContext(null);

export const SessionProvider = ({ children }) => {
  const { auth, role, isAuthenticated, authChecked } = useSelector((state) => ({
    auth: state.auth.auth,
    role: state.auth.role,
    isAuthenticated: state.auth.isAuthenticated,
    authChecked: state.auth.authChecked,
  }));

  const session = useMemo(() => {
    // Get token from localStorage
    const getUserDetails = () => {
      const userDetails = localStorage.getItem("userDetails");
      if (userDetails) {
        try {
          return JSON.parse(userDetails);
        } catch (e) {
          return null;
        }
      }
      return null;
    };

    const userDetails = getUserDetails();
    const token = userDetails?.access_token || null;
    const refreshToken = userDetails?.refresh_token || null;
    const tokenExpiry = userDetails?.expireDate || null;

    // Get decoded token data
    const decodedToken = token ? decodeToken(token) : null;

    // Get user info from decoded token
    const user = decodedToken
      ? {
          email: decodedToken.email || "",
          name: decodedToken.name || decodedToken.preferred_username || "",
          userId: decodedToken.sub || "",
          roles: decodedToken.realm_access?.roles || [],
        }
      : null;

    // Check if token is expired
    const isTokenExpired = () => {
      if (!tokenExpiry) return true;
      const expiryTime = new Date(tokenExpiry);
      const now = new Date();
      return now > expiryTime;
    };

    return {
      // Authentication state
      isAuthenticated,
      authChecked,
      role,

      // Auth data from Redux
      auth,

      // Token information
      token,
      refreshToken,
      tokenExpiry,
      isTokenExpired: isTokenExpired(),

      // Decoded token
      decodedToken,

      // User information
      user,

      // Helper methods
      getToken: () => token,
      getRefreshToken: () => refreshToken,
      getUserDetails: getUserDetails,
    };
  }, [auth, role, isAuthenticated, authChecked]);

  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  );
};

// Custom hook to use session
export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return context;
};

export default SessionContext;
